import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import DialogContentText from '@mui/material/DialogContentText';
import { Trans, useTranslation } from 'react-i18next';
import {
  Alert,
  CheckboxV2,
  Dialog,
  TextV2,
} from '@insights-ltd/design-library/components';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';

type ConfirmDeleteDialogProps = {
  open: boolean;
  isLoading: boolean;
  isError: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const ConfirmDeleteDialog = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  isError,
}: ConfirmDeleteDialogProps) => {
  const { t } = useTranslation();
  const [check1, setCheck1] = useState(false);
  const [check2, setCheck2] = useState(false);
  const [check3, setCheck3] = useState(false);
  const [errorMessageKey, setErrorMessageKey] = useState('');

  useEffect(() => {
    if (isError) {
      setErrorMessageKey(
        'ui.event-management.events.learner.confirm-delete.error',
      );
    } else {
      setErrorMessageKey('');
    }
  }, [check1, check2, check3, isError]);

  useEffect(() => {
    setCheck1(false);
    setCheck2(false);
    setCheck3(false);
  }, [open]);

  const handleConfirm = () => {
    if (check1 && check2 && check3) {
      onConfirm();
    } else {
      setErrorMessageKey(
        'ui.event-management.events.learner.confirm-delete.validation-error',
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('ui.event-management.learner.edit.delete-learner')}
      aria-labelledby="event-created-dialog-title"
      aria-describedby="alert-dialog-description"
      content={
        <>
          <DialogContentText id="alert-dialog-description">
            <Trans
              i18nKey="ui.event-management.events.learner.confirm-delete.from.organisation.body.bold"
              components={{
                bold: <TextV2 component="span" variant="bodyBold" />,
              }}
            />
          </DialogContentText>
          <Box
            sx={[
              {
                display: 'flex',
                flexDirection: 'column',
              },
              (theme) => ({
                gap: theme.spacing(spacingSizeMap.S),
                '& .MuiFormControlLabel-root': {
                  alignItems: 'flex-start',
                },
              }),
            ]}
          >
            <TextV2>
              {t('ui.event-management.events.learner.confirm-delete.heading')}
            </TextV2>
            <FormControlLabel
              control={
                <CheckboxV2
                  id="confirm-delete-1"
                  name="confirmDelete1"
                  checked={check1}
                  onChange={setCheck1}
                />
              }
              label={
                <TextV2
                  color="error"
                  variant="bodyBold"
                  sx={(theme) => ({
                    display: 'block',
                    marginLeft: theme.spacing(spacingSizeMap.XS),
                  })}
                >
                  {t(
                    'ui.event-management.events.learner.confirm-delete.item-1',
                  )}
                </TextV2>
              }
            />
            <FormControlLabel
              control={
                <CheckboxV2
                  id="confirm-delete-2"
                  name="confirmDelete2"
                  checked={check2}
                  onChange={setCheck2}
                />
              }
              label={
                <TextV2
                  color="error"
                  variant="bodyBold"
                  sx={(theme) => ({
                    display: 'block',
                    marginLeft: theme.spacing(spacingSizeMap.XS),
                  })}
                >
                  {t(
                    'ui.event-management.events.learner.confirm-delete.item-2',
                  )}
                </TextV2>
              }
            />
            <FormControlLabel
              control={
                <CheckboxV2
                  id="confirm-delete-3"
                  name="confirmDelete3"
                  checked={check3}
                  onChange={setCheck3}
                />
              }
              label={
                <TextV2
                  color="error"
                  variant="bodyBold"
                  sx={(theme) => ({
                    display: 'block',
                    marginLeft: theme.spacing(spacingSizeMap.XS),
                  })}
                >
                  {t(
                    'ui.event-management.events.learner.confirm-delete.item-3',
                  )}
                </TextV2>
              }
            />
          </Box>
          {errorMessageKey !== '' && (
            <>
              <Box
                sx={{
                  mb: (theme) => theme.spacing(spacingSizeMap.M),
                }}
              />
              <Alert severity="error" data-testid="error-message">
                <TextV2
                  component="span"
                  sx={{ color: 'inherit', fontWeight: 'inherit' }}
                >
                  {t(errorMessageKey)}
                </TextV2>
              </Alert>
            </>
          )}
        </>
      }
      onSubmit={handleConfirm}
      primaryButtonText={t('ui.event-management.learner.edit.delete-learner')}
      primaryButtonColour="error"
      disablePrimaryButton={isLoading}
      secondaryButtonText={t(
        'ui.event-management.events.add-learners.cancel-upload.button',
      )}
    />
  );
};

export default ConfirmDeleteDialog;
